import { ChangeDetectorRef, Component, Inject, Optional } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { MatLegacyDialogRef as MatDialogRef, MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA } from '@angular/material/legacy-dialog';
import { ActivatedRoute } from '@angular/router';
import * as moment from 'moment';
import { Observable, of } from 'rxjs';
import { first } from 'rxjs/operators';
import { BusinessTransactionType } from '../../../../../foundation/models/enums/business-transaction-type.enum';
import { BaseComponent } from '../../../../../shared/base-components/base.component';
import { getServerDate, setServerDate } from '../../../../../shared/helpers/time-helpers.model';
import { ControlValidationMessagesService } from '../../../../../shared/validators/control-validation-messages.service';
import { EmissionAreaTypeTestTypeTestResultTypeCertificateTypeMap } from '../../../../../titleregistration/models/emission-areaType-testType-testResultType-certificateType-map';
import { EmissionTestTypeAreaTypeMap } from '../../../../../titleregistration/models/emission-test-type-area-type-map.model';
import { EmissionTestTypeTestResultTypeMap } from '../../../../../titleregistration/models/emission-test-type-test-result-type-map.model';
import { EmissionTestTypeModel } from '../../../../../titleregistration/models/emission-test-type.model';
import { EmissionAreaType, IEmissionAreaType } from '../../../../../titleregistration/models/enums/emission-area-type.enum';
import { EmissionCertificateType, IEmissionCertificateType } from '../../../../../titleregistration/models/enums/emission-certificate-type.enum';
import { EmissionTestResultType, IEmissionTestResultType } from '../../../../../titleregistration/models/enums/emission-test-result-type.enum';
import { EmissionTestType, IEmissionTestType } from '../../../../../titleregistration/models/enums/emission-test-type.enum';
import { OwnershipCompositeServiceEnum } from '../../../../../titleregistration/models/enums/owership-composite.enum';
import { VehicleEmissionCheckStatusType } from '../../../../../titleregistration/models/enums/vehicle-emission-check-status-type.enum';
import { RegistrationModel } from '../../../../../titleregistration/models/registration.model';
import { VehicleEmissionTestModel } from '../../../../../titleregistration/models/vehicle-emission-test.model';
import { VehicleOwnershipModel } from '../../../../../titleregistration/models/vehicle-ownership.model';
import { TitleRegistrationService } from '../../../../../titleregistration/services/titleregistration.service';
import { VehicleEmissionsService } from '../../../../../titleregistration/services/vehicle-emissions.service';
import { VehicleType } from '../../../../models/enums/vehicle-type.enum';
import { VehicleModel } from '../../../../models/vehicle.model';
import { VehicleEmissionCheckStatusRegistrationModel } from '../../../../../titleregistration/models/vehicle-emission-check-status-registration.model';

export class VehicleEmissionsModalData {
    public copyEmissionsTest: VehicleEmissionTestModel;
    public vehicle: VehicleModel;
}

@Component({
    selector: 'app-vehicle-emissions-modal',
    templateUrl: './vehicle-emissions-modal.component.html',
    styles: []
})
export class VehicleEmissionsModalComponent extends BaseComponent {
    public action: string;
    public dataLoaded = false;
    private registrationModel: RegistrationModel;
    /**
     * Check status type
     */
    public checkStatusTypeID: number;

    public transactionTypeId: number;

    /**
     * Enum for emission test result types
     */
    private emissionTestResultType: EmissionTestResultType = new EmissionTestResultType();

    /**
     * Enum for emission test types
     */
    private emissionTestType: EmissionTestType = new EmissionTestType();

    /**
     * Enum for emission area types
     */
    private emissionAreaType: EmissionAreaType = new EmissionAreaType();

    /**
     * Enum for emission certificate types
     */
    private emissionCertificateType: EmissionCertificateType = new EmissionCertificateType();

    /**
     * Enum for the vehicle emission check status type
     */
    private vehicleEmissionCheckStatusType: VehicleEmissionCheckStatusType = new VehicleEmissionCheckStatusType();

    /**
     * Array of all emission test result types
     */
    private emissionTestResultTypes: IEmissionTestResultType[];

    /**
     * Array of all emission test types
     */
    private emissionTestTypes: IEmissionTestType[];

    /**
     * Array of all emission area types
     */
    private emissionAreaTypes: IEmissionAreaType[];

    /**
     * Array of all emission certificate types
     */
    private emissionCertificateTypes: IEmissionCertificateType[];

    /**
     * Control variable for if the expiration date is criteria is untouched
     */
    private expirationSet: boolean;

    /**
     * Emissions test model.
     */
    public emissions: VehicleEmissionTestModel;

    /**
     * Array of mappings for area types to test types
     */
    private emissionMaps: EmissionTestTypeAreaTypeMap[];

    /**
     * Array of mappings for area types to test types
     */
    private resultTypeMaps: EmissionTestTypeTestResultTypeMap[];

    /**
     * Array of mappings for area types to test types
     */
    private emissionAreaTypeTestTypeTestResultTypeCertificateTypeMaps: EmissionAreaTypeTestTypeTestResultTypeCertificateTypeMap[];

    /**
     * Validation form group
     */
    public form: UntypedFormGroup;

    private businessTransactionType: BusinessTransactionType = new BusinessTransactionType();

    private vehicleType: VehicleType = new VehicleType();

    public isCommuteToNonAttainmentArea: boolean;

    constructor(
        private titleRegistrationService: TitleRegistrationService,
        private vehicleEmissionsService: VehicleEmissionsService,
        protected changeDetectionRef: ChangeDetectorRef,
        protected fb: UntypedFormBuilder,
        private route: ActivatedRoute,
        public dialogRef: MatDialogRef<VehicleEmissionsModalData>,
        @Optional() @Inject(MAT_DIALOG_DATA) public data: VehicleEmissionsModalData
    ) {
        super();
    }

    /**
     * Initializes the component
     */
    public async ngOnInit() {
        this.form = new UntypedFormGroup({});
        let ownership = await this.titleRegistrationService.getActiveVehicleOwnership(this.data.vehicle.id, OwnershipCompositeServiceEnum.Registration).pipe(first()).toPromise() as VehicleOwnershipModel;
        if (ownership && ownership.vehicleOwnershipPlate && ownership.vehicleOwnershipPlate.registration) {
            this.registrationModel = ownership.vehicleOwnershipPlate.registration;
        }

        if (this.data.copyEmissionsTest) {
            this.emissions = this.data.copyEmissionsTest;
            this.emissions.id = -1;
        } else {
            this.emissions = new VehicleEmissionTestModel();
            this.emissions.id = -1;
            this.emissions.emissionCertificateTypeID = this.emissionCertificateType.VehicleInspectionReport.id;
            this.emissions.emissionAreaTypeID = null;
            this.emissions.emissionTestTypeID = null;
            this.emissions.emissionTestResultTypeID = null;
            this.emissions.modified = false;
        }

        if (this.emissions.id <= 0) {
            const vehicle = Object.assign({}, this.data.vehicle);
            if ((vehicle.vehicleTypeID === this.vehicleType.Trailer.id) || (vehicle.vehicleTypeID === this.vehicleType.TentTrailer.id) || (vehicle.vehicleTypeID === this.vehicleType.TravelTrailer.id)) {
                this.checkStatusTypeID = 15;
            }
        }

        if (+this.emissions.emissionCertificateTypeID === 0) {
            this.emissions.emissionCertificateTypeID = this.emissionCertificateType.VehicleInspectionReport.id;
        }

        this.emissions.vehicleId = this.data.vehicle.id;
        try {
            let emissionMapResults = await this.vehicleEmissionsService.getEmissionsTestTypeAreaTypeMaps().pipe(first()).toPromise();
            let resultTypeMapResults = await this.vehicleEmissionsService.getEmissionTestTypeTestResultTypeMaps().pipe(first()).toPromise();
            let emissionAreaTypeTestTypeTestResultTypeCertificateTypeMapResults = await this.vehicleEmissionsService.getEmissionAreaTypeTestTypeTestResultTypeCertificateTypeMap().pipe(first()).toPromise();
            this.emissionMaps = <EmissionTestTypeAreaTypeMap[]> emissionMapResults;
            this.resultTypeMaps = <EmissionTestTypeTestResultTypeMap[]> resultTypeMapResults;
            this.emissionAreaTypeTestTypeTestResultTypeCertificateTypeMaps = <EmissionAreaTypeTestTypeTestResultTypeCertificateTypeMap[]> emissionAreaTypeTestTypeTestResultTypeCertificateTypeMapResults;
            this.emissionAreaTypes = this.emissionAreaType.GetAll();
            this.emissionCertificateTypes = this.emissionCertificateType.GetAll();
            this.emissionTestTypes = this.emissionTestType.GetAll();
            this.emissionTestResultTypes = this.emissionTestResultType.GetAll();

            this.form.addControl('initialTest', new UntypedFormControl({ value: this.emissions.initialTest, disabled: false }, Validators.compose([])));
            this.form.addControl('initialTestDate', new UntypedFormControl({ value: getServerDate(this.emissions, 'initialTestDate'), disabled: false }, ControlValidationMessagesService.shortDateValidator));
            this.form.addControl('passedDate', new UntypedFormControl({ value: getServerDate(this.emissions, 'testPassedDate'), disabled: false }, ControlValidationMessagesService.shortDateValidator));
            this.form.addControl('expirationDate', new UntypedFormControl({ value: getServerDate(this.emissions, 'testExpirationDate'), disabled: this.emissions.emissionCertificateTypeID !== this.emissionCertificateType.CertificateOfInspection.id }, ControlValidationMessagesService.shortDateValidator));
            this.form.addControl('emissionAreaType', new UntypedFormControl({ value: this.emissions.emissionAreaTypeID, disabled: this.emissions.isDomicileAddressExempted }, Validators.compose([])));
            this.form.addControl('testType', new UntypedFormControl({ value: this.emissions.emissionTestTypeID, disabled: this.getFilteredTestTypes().length === 0 }, Validators.compose([])));
            this.form.addControl('testResultType', new UntypedFormControl({ value: this.emissions.emissionTestResultTypeID, disabled: false }, Validators.compose([])));
            this.form.addControl('certificateType', new UntypedFormControl({ value: this.emissions.emissionCertificateTypeID, disabled: false }, Validators.compose([])));

            this.dataLoaded = true;
        } catch (e) {
            console.log(e);
        }

        this.detectFormChanges();
    }

    /**
     * Calculates the expiration date based on the values entered on the form.
     */
    public calculateExpirationDate = async (): Promise<void> => {
        let expiration = this.registrationModel ? this.registrationModel.registrationEndDate : moment(new Date()).add(1, 'y');
        let certificateTypeId = this.form.get('certificateType').value ? +this.form.get('certificateType').value : 0;
        let emissionAreaTypeId = this.form.get('emissionAreaType').value ? +this.form.get('emissionAreaType').value : null;
        let testResultTypeId = this.form.get('testResultType').value ? +this.form.get('testResultType').value : null;
        let testTypeId = this.form.get('testType').value ? +this.form.get('testType').value : null;
        let initialTestDate = this.form.get('initialTestDate').value;
        let testPassedDate = this.form.get('passedDate').value;
        let testExpirationDateControl = this.form.get('expirationDate');

        if (certificateTypeId === this.emissionCertificateType.Waiver.id || certificateTypeId === this.emissionCertificateType.DirectorsCertificate.id) {
            testExpirationDateControl.setValue(moment(expiration).format('L'));
        } else if (certificateTypeId !== this.emissionCertificateType.CertificateOfInspection.id) {
            if (certificateTypeId && emissionAreaTypeId && testResultTypeId &&
                testTypeId && initialTestDate && testPassedDate && !this.expirationSet) {
                try {
                    testExpirationDateControl.setValue(null);
                    this.updateEmissionsModelWithFormValues();
                    const returnValue = await this.vehicleEmissionsService.calculateEmissionExpiration(this.emissions).pipe(first()).toPromise();
                    if (returnValue) {
                        const expDate = moment(returnValue).toDate();
                        if (expDate.getDate() === 1 && expDate.getMonth() === 0 && expDate.getFullYear() === 1) {
                            testExpirationDateControl.setValue(null);
                        } else {
                            testExpirationDateControl.setValue(moment(expDate).format('L'));
                        }

                        this.expirationSet = true;
                    } else {
                        testExpirationDateControl.setValue(null);
                        this.expirationSet = true;
                    }
                } catch (e) {
                    console.log(e);
                }
            } else if (emissionAreaTypeId === this.emissionAreaType.EmissionsExempt.id) {
                testExpirationDateControl.setValue(null);
            }
        }
    };

    /**
     * Event handler for the test type changing.
     * @param selectedValue: Value selected by the user
     */
    public onTestTypeChange = async (selectedValue: number): Promise<void> => {
        this.expirationSet = false;

        await this.calculateExpirationDate();
    };

    /**
     * Event handler for the test result type changing.
     * @param selectedValue: Value selected by the user
     */
    public onTestResultChange = async (selectedValue: number): Promise<void> => {
        this.emissions.modified = true;
        this.expirationSet = false;

        await this.calculateExpirationDate();
    };

    /**
     * Event handler for the test area changing.
     * @param selectedValue: Value selected by the user
     */
    public onTestAreaChange = async (selectedValue: number): Promise<void> => {
        if (+selectedValue) {
            if (+selectedValue === this.emissionAreaType.EmissionsExempt.id) {
                this.form.get('initialTestDate').setValue(setServerDate(moment().toDate()));
            }

            this.form.get('testType').enable();
        } else {
            this.form.get('testType').disable();
        }

        this.emissions.modified = true;
        this.emissions.id = -1;

        this.expirationSet = false;
        await this.calculateExpirationDate();
    };

    /**
     * Gets the emissions areas
     */
    public getEmissionAreaTypes = (): void => {
        let filteredAreas = null;
        if (this.isCommuteToNonAttainmentArea) {
            filteredAreas = this.emissionAreaTypes.filter(x => +x.id !== this.emissionAreaType.EmissionsExempt.id);
        } else {
            filteredAreas = this.emissionAreaTypes;
        }

        return filteredAreas;
    };

    /**
     * Gets the valid test types based on the selected area.
     */
    public getFilteredTestTypes = (): IEmissionTestType[] => {
        const self = this;
        const result = new Array<EmissionTestTypeModel>();
        let areaTypeId = this.form.get('emissionAreaType').value ? +this.form.get('emissionAreaType').value : null;
        if (areaTypeId === this.emissionAreaType.EmissionsExempt.id) {
            return this.emissionTestType.GetAll();
        } else {
            const result = new Array<IEmissionTestType>();
            if (areaTypeId > 0) {
                for (var j = 0; j < self.emissionMaps.length; j++) {
                    var emissionMap = <EmissionTestTypeAreaTypeMap> self.emissionMaps[j];
                    if (+emissionMap.emissionAreaTypeId === areaTypeId) {
                        const item = self.emissionTestTypes.find(x => +x.id === +emissionMap.emissionTestTypeId);
                        result.push(item);
                    }
                }
            }

            return result;
        }
    };

    public getFilteredResultTypes = (): IEmissionTestResultType[] => {
        let result = new Array<IEmissionTestResultType>();
        let testTypeId = this.form.get('testType').value ? +this.form.get('testType').value : null;
        if (testTypeId > 0) {
            for (var j = 0; j < this.resultTypeMaps.length; j++) {
                var emissionMap = <EmissionTestTypeTestResultTypeMap> this.resultTypeMaps[j];
                if (+emissionMap.emissionTestTypeId === testTypeId) {
                    let item = this.emissionTestResultTypes.find(x => +x.id === +emissionMap.emissionTestResultTypeId);
                    result.push(item);
                }
            }
        }

        return result;
    };

    /**
     * Gets the list of valid emission certificate types
     */
    public getFilteredEmissionCertificateTypes = (): IEmissionCertificateType[] => {
        if (this.transactionTypeId !== this.businessTransactionType.TitleandRegistration.id && this.transactionTypeId !== this.businessTransactionType.TnRCorrections.id) {
            let areaTypeId = this.form.get('emissionAreaType').value ? +this.form.get('emissionAreaType').value : null;
            let testResultTypeId = this.form.get('testResultType').value ? +this.form.get('testResultType').value : null;
            let testTypeId = this.form.get('testType').value ? +this.form.get('testType').value : null;
            var filterCertificateTypes = this.emissionAreaTypeTestTypeTestResultTypeCertificateTypeMaps.filter(m => m.emissionAreaTypeId === areaTypeId && m.emissionTestResultTypeId === testResultTypeId && m.emissionTestTypeId === testTypeId);
            var certificateTypeIds = filterCertificateTypes.map(s => s.emissionCertificateTypeId);
            let filteredTypes = this.emissionCertificateTypes.filter(s => certificateTypeIds.find(l => l === s.id) != null);
            return filteredTypes.filter(e => e.id !== this.emissionCertificateType.CertificateOfInspection.id);
        } else {
            return this.emissionCertificateTypes;
        }
    };

    /**
     * Event handler for the certificate type changing.
     * @param selectedValue: Value selected by the user
     */
    public onCertificateTypeChange = async (selectedValue: number): Promise<void> => {
        this.expirationSet = false;
        this.emissions.modified = true;
        const disabled = this.form.get('expirationDate').disabled;
        let certificateTypeId = this.form.get('certificateType').value ? +this.form.get('certificateType').value : 0;
        let expirationDateControl = this.form.get('expirationDate');
        if (certificateTypeId === this.emissionCertificateType.CertificateOfInspection.id) {
            expirationDateControl.setValue(null);
            expirationDateControl.enable();
        } else {
            if (!disabled) {
                expirationDateControl.setValue(null);
            }

            expirationDateControl.disable();
        }

        await this.calculateExpirationDate();
    };

    /**
     * Event handler for the initial test date changed.
     * @param selectedValue: Value selected by the user
     */
    public onInitialTestDateChanged = async (eventDate: Date): Promise<void> => {
        this.expirationSet = false;
        this.emissions.modified = true;
        await this.calculateExpirationDate();
        this.detectFormChanges();
    };

    /**
     * Event handler for test passed date changed.
     * @param selectedValue: Value selected by the user
     */
    public onTestPassedDateChanged = async (eventDate: Date): Promise<void> => {
        this.expirationSet = false;
        this.emissions.modified = true;
        await this.calculateExpirationDate();
        this.detectFormChanges();
    };

    /**
     * Marks the expiration date as needed to be recalculated
     */
    public onInitialTestDateInput = (event: Event): void => {
        this.expirationSet = false;
        this.emissions.modified = true;
    };

    /**
     * Marks the expiration date as needed to be recalculated
     */
    public onTestPassedDateInput = (event: Event): void => {
        this.expirationSet = false;
        this.emissions.modified = true;
        this.emissions.id = -1;
    };

    /**
     * Copies the expiration date to the model.
     */
    public onExpirationDateInput = (): void => {
        this.emissions.modified = true;
    };

    /**
     * Saves the data entered on the form
     */
    public save = (): Observable<boolean> => {
        if (this.form.valid) {
            let checkStatusModel: VehicleEmissionCheckStatusRegistrationModel = null;
            if (this.registrationModel && this.registrationModel.vehicleEmissionCheckStatusRegistration) {
                checkStatusModel = this.registrationModel.vehicleEmissionCheckStatusRegistration;
            } else {
                checkStatusModel = new VehicleEmissionCheckStatusRegistrationModel();
            }

            if (!checkStatusModel.vehicleEmissionCheckStatusTypeID) {
                checkStatusModel.vehicleEmissionCheckStatusTypeID = this.vehicleEmissionCheckStatusType.EmissionsValid.id;
            }

            this.updateEmissionsModelWithFormValues();

            checkStatusModel.vehicleEmissionTest = this.emissions;
            this.emissions.id = this.emissions.id > 0 ? this.emissions.id : -1;

            if (+this.emissions.emissionCertificateTypeID === 0) {
                this.emissions.emissionCertificateTypeID = this.emissionCertificateType.VehicleInspectionReport.id;
            }

            this.vehicleEmissionsService.saveVehicleEmissionTest(checkStatusModel)
                .subscribe(
                    (p) => {
                        this.dialogRef.close(p);
                    },
                    (error) => {
                        this.handleValidationResult(error);
                    });
        } else {
            return of(false);
        }
    };

    /**
     * Sets the commute to non-attainment area.
     * @param checked: Value of the checkbox
     */
    public setCommuteToNonAttainmentArea = (checked: boolean): void => {
        this.isCommuteToNonAttainmentArea = checked;
    };

    public showRequired = (): boolean => {
        if (this.checkStatusTypeID === this.vehicleEmissionCheckStatusType.EmissionsTestExempt1st5Yr.id || this.checkStatusTypeID === this.vehicleEmissionCheckStatusType.EmissionsTestExempt.id) {
            return false;
        }

        let emissionAreaTypeId = this.form.get('emissionAreaType').value ? +this.form.get('emissionAreaType').value : null;
        if (emissionAreaTypeId === this.emissionAreaType.EmissionsExempt.id) {
            return false;
        }

        return true;
    };

    public showAttainmentRequired = (): boolean => {
        if (this.checkStatusTypeID === this.vehicleEmissionCheckStatusType.EmissionsTestExempt1st5Yr.id || this.checkStatusTypeID === this.vehicleEmissionCheckStatusType.EmissionsTestExempt.id) {
            return false;
        }

        return true;
    };

    /**
     * Force angular to check for changes.
     */
    private detectFormChanges = (): void => {
        this.form.updateValueAndValidity();
        this.changeDetectionRef.detectChanges();
    };

    public cancel(): void {
        this.dialogRef.close();
    }

    private updateEmissionsModelWithFormValues() {
        this.emissions.initialTest = this.form.get('initialTest').value;
        this.emissions.initialTestDate = this.form.get('initialTestDate').value ? setServerDate(this.form.get('initialTestDate').value) : null;
        this.emissions.testPassedDate = this.form.get('passedDate').value ? setServerDate(this.form.get('passedDate').value) : null;
        this.emissions.testExpirationDate = this.form.get('expirationDate').value ? setServerDate(this.form.get('expirationDate').value) : null;
        this.emissions.emissionTestResultTypeID = this.form.get('testResultType').value ? +this.form.get('testResultType').value : null;
        this.emissions.emissionTestTypeID = this.form.get('testType').value ? +this.form.get('testType').value : null;
        this.emissions.emissionAreaTypeID = this.form.get('emissionAreaType').value ? +this.form.get('emissionAreaType').value : null;
        this.emissions.emissionCertificateTypeID = this.form.get('certificateType').value ? +this.form.get('certificateType').value : 0;
    }
}
